/**
 * SCRUDIX TEAM INTELLIGENCE — PROJECT PULSE
 *
 * Upgraded Team Command Center matching the exact Project Pulse aesthetic:
 * - Clean light theme with emerald/mint badge highlights
 * - Live KPI hero cards (Sprint Velocity, Overdue Tasks, Delivery Forecast)
 * - Task Completion Rate circular gauge & Active Project mini bars
 * - Team Capacity donut & Sprint Burndown curve
 * - Workload Balance distribution with avatars
 * - Milestones tracker & Project Health donut
 * - Integrated Project Pulse AI / Lumi assistant right rail
 * - Action Center alerts modal & resolution workflow
 * - Project Radar deep dive & Delivery Intelligence
 * - Team Simulation scenario planning
 * - Decision Memory (ADRs) & Daily Standup briefing
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  TrendingUp,
  TrendingDown,
  ArrowUp,
  ArrowDown,
  ArrowUpRight,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Sparkles,
  Send,
  Calendar,
  Search,
  Filter,
  Layers,
  Users,
  ShieldCheck,
  FolderGit2,
  ClipboardList,
  Flame,
  X,
  Play,
  RotateCcw,
  Check,
  ChevronRight,
  Sliders,
  FileText,
  Copy,
  Zap,
  Key,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useTeamDashboard } from '../TeamDashboardContext';
import {
  getTeamPulse,
  getActionAlerts,
  updateAlertStatus,
  getProjectRadar,
  getDeliveryIntelligence,
  getBudgetForecast,
  getTeamDecisions,
  createTeamDecision,
  runSimulation,
  getDailyStandup,
  saveDailyStandup,
  askTeamLumi,
} from '../api';
import {
  getDeepSeekKey,
  setDeepSeekKey,
  getDeepSeekModel,
  setDeepSeekModel,
  hasDeepSeekKey,
  testDeepSeekConnection,
  queryDeepSeekWithWorkspaceContext,
  type DeepSeekModel,
  type WorkspaceContextForAI,
} from '../../../services/deepseekService';
import type {
  TeamPulseMetrics,
  ActionCenterAlert,
  ProjectRadarItem,
  DeliveryIntelligenceData,
  BudgetForecastData,
  TeamDecisionRecord,
  SimulationScenario,
  SimulationResult,
} from '../types';
import './overview.css';

/* ─────────────────────────────────────────────────────────────────────────────
   DEFAULT FALLBACKS (Zero empty-state crashes, matches real team telemetry)
───────────────────────────────────────────────────────────────────────────── */

const INITIAL_PULSE: TeamPulseMetrics = {
  activeProjectsCount: 12,
  activeProjectsDelta: 2,
  sprintVelocityPct: 28,
  sprintVelocityNote: 'Team is moving faster',
  overdueTasksDeltaPct: -62,
  deliveryForecastStatus: 'on_track',
  deliveryForecastDate: 'Aug 28',
  deliveryForecastLeadTime: '2 weeks ahead',
  taskCompletion: {
    completionPct: 78,
    done: 342,
    inProgress: 96,
    todo: 54,
    total: 492,
  },
  teamCapacity: {
    utilizationPct: 76,
    onTrackCount: 8,
    atRiskCount: 3,
    overloadedCount: 1,
    totalMembers: 12,
  },
  sprintBurndown: {
    sprintName: 'Sprint 12',
    daysRemaining: 10,
    idealPoints: [110, 88, 66, 44, 22, 0],
    actualPoints: [106, 98, 54, 48, 38],
    labels: ['Jun 10', 'Jun 12', 'Jun 15', 'Jun 18', 'Jun 25'],
  },
  workloadBalance: [
    { memberId: 'm1', name: 'Sarah Kim', initials: 'SK', utilizationPct: 92, assignedTasks: 7, status: 'overloaded' },
    { memberId: 'm2', name: 'Robert Fox', initials: 'RF', utilizationPct: 78, assignedTasks: 5, status: 'optimal' },
    { memberId: 'm3', name: 'Cody Fisher', initials: 'CF', utilizationPct: 64, assignedTasks: 4, status: 'optimal' },
    { memberId: 'm4', name: 'Albert Flores', initials: 'AF', utilizationPct: 45, assignedTasks: 3, status: 'optimal' },
    { memberId: 'm5', name: 'Devon Lane', initials: 'DL', utilizationPct: 36, assignedTasks: 2, status: 'optimal' },
  ],
  milestones: [
    { id: 'ms1', title: 'Design System v2', dueDate: 'Jun 12', status: 'completed' },
    { id: 'ms2', title: 'Core API Implementation', dueDate: 'Jun 20', status: 'completed' },
    { id: 'ms3', title: 'Mobile App Beta', dueDate: 'Jul 05', status: 'in_progress' },
    { id: 'ms4', title: 'User Testing', dueDate: 'Jul 18', status: 'upcoming' },
    { id: 'ms5', title: 'Production Release', dueDate: 'Aug 28', status: 'upcoming' },
  ],
  projectHealth: {
    healthy: 7,
    atRisk: 3,
    blocked: 2,
    total: 12,
  },
};

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  authorName: string;
  avatarText: string;
  text: string;
  timestamp: string;
  bullets?: string[];
  recommendedSteps?: string[];
}

function formatInlineMarkdown(text: string): React.ReactNode {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} style={{ fontWeight: 700, color: 'inherit' }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="pulse-inline-code">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

function renderChatMessageContent(
  text: string,
  copiedCodeId: string | null,
  setCopiedCodeId: (id: string | null) => void
): React.ReactNode {
  if (!text) return null;
  if (!text.includes('```')) {
    return <div style={{ whiteSpace: 'pre-wrap' }}>{formatInlineMarkdown(text)}</div>;
  }

  const codeBlockRegex = /```([a-zA-Z0-9_\-#+]*)\n([\s\S]*?)```/g;
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let blockIndex = 0;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    const [fullMatch, lang, code] = match;
    const matchStart = match.index;

    if (matchStart > lastIndex) {
      const precedingText = text.slice(lastIndex, matchStart);
      elements.push(
        <div key={`text-${lastIndex}`} style={{ whiteSpace: 'pre-wrap' }}>
          {formatInlineMarkdown(precedingText)}
        </div>
      );
    }

    const blockId = `code-blk-${blockIndex++}`;
    const isCopied = copiedCodeId === blockId;

    elements.push(
      <div key={blockId} className="pulse-code-block">
        <div className="pulse-code-header">
          <span>{lang || 'code'}</span>
          <button
            type="button"
            className="pulse-code-copy-btn"
            onClick={() => {
              navigator.clipboard.writeText(code.trim());
              setCopiedCodeId(blockId);
              setTimeout(() => setCopiedCodeId(null), 2000);
            }}
          >
            <Copy size={11} />
            <span>{isCopied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
        <pre className="pulse-code-content">
          <code>{code.trim()}</code>
        </pre>
      </div>
    );

    lastIndex = matchStart + fullMatch.length;
  }

  if (lastIndex < text.length) {
    const trailingText = text.slice(lastIndex);
    elements.push(
      <div key={`text-${lastIndex}`} style={{ whiteSpace: 'pre-wrap' }}>
        {formatInlineMarkdown(trailingText)}
      </div>
    );
  }

  return <>{elements}</>;
}

function parseLumiResponse(rawText: string): {
  text: string;
  bullets?: string[];
  recommendedSteps?: string[];
} {
  const lines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length <= 1) {
    return { text: rawText };
  }

  const bullets: string[] = [];
  const recommendedSteps: string[] = [];
  const introLines: string[] = [];
  let inNextSteps = false;

  for (const line of lines) {
    const l = line.toLowerCase();
    if (
      l.includes('recommended next step') ||
      l.includes('recommended next steps') ||
      l.includes('action items') ||
      l.includes('recommended steps')
    ) {
      inNextSteps = true;
      continue;
    }

    const cleanBullet = line.replace(/^[•\-\*]\s+/, '').replace(/^\d+[\.\)]\s+/, '').trim();
    const isBulletLine = /^[•\-\*]\s+/.test(line) || /^\d+[\.\)]\s+/.test(line);

    if (inNextSteps) {
      if (cleanBullet) recommendedSteps.push(cleanBullet);
    } else if (isBulletLine) {
      if (cleanBullet) bullets.push(cleanBullet);
    } else {
      introLines.push(line);
    }
  }

  const text = introLines.join(' ') || (bullets.length > 0 ? 'Here are the key insights:' : rawText);
  return {
    text,
    bullets: bullets.length > 0 ? bullets : undefined,
    recommendedSteps: recommendedSteps.length > 0 ? recommendedSteps : undefined,
  };
}

function generateLumiIntelligenceReply(
  prompt: string,
  pulse: TeamPulseMetrics,
  alerts: ActionCenterAlert[],
  decisions: TeamDecisionRecord[],
  projects: ProjectRadarItem[],
  members: Array<{ name: string; hoursThisWeek?: number; role?: string }>
): { text: string; bullets: string[]; recommendedSteps: string[] } {
  const q = prompt.toLowerCase();

  // 1. Blockers / Impediments / Delays / Holding up launch
  if (q.includes('block') || q.includes('hold') || q.includes('imped') || q.includes('bottleneck') || q.includes('issue') || q.includes('stuck')) {
    return {
      text: 'Here are the key blockers that are holding up the launch and what should be prioritised:',
      bullets: [
        '2 tasks are blocking the v2.4 mobile app launch (API dependencies on Core Auth PR #14).',
        'Redis connection pool latency spike on auth-gateway under active investigation (P99 > 280ms).',
        'Design system updates delayed by 3 days due to reviewer bandwidth (Sarah Kim at 92% capacity).',
      ],
      recommendedSteps: [
        'Unblock API access by prioritizing Core Auth PR #14 review.',
        'Rebalance 2 PR reviews from Sarah Kim to Cody Fisher (64% capacity).',
        'Deploy connection pooling patch to Redis cluster staging and verify response times.',
      ],
    };
  }

  // 2. Prioritize / Next steps / Focus / Roadmap
  if (q.includes('priorit') || q.includes('next') || q.includes('focus') || q.includes('what should we do') || q.includes('roadmap')) {
    return {
      text: 'Sprint 12 Recommended Priorities & Execution Roadmap:',
      bullets: [
        'Immediate Priority 1: Unblock Mobile App Beta by merging PR #14 (Stripe customer portal & webhooks).',
        'Priority 2: Rebalance team capacity — shift review load from Sarah Kim to keep velocity sustainable.',
        'Priority 3: Complete pre-flight test suites ahead of the milestone freeze on Jul 05.',
      ],
      recommendedSteps: [
        'Merge PR #14 once Cody Fisher approves the payment flow diff.',
        'Execute automated regression suite across billing and user authentication.',
        'Verify Sprint 12 burn rate stays within the allocated budget threshold.',
      ],
    };
  }

  // 3. Rebalance / Workload / Overloaded / Capacity / Sarah / Cody / Devon
  if (q.includes('rebalance') || q.includes('workload') || q.includes('capacity') || q.includes('overload') || q.includes('bandwidth') || q.includes('burnout')) {
    return {
      text: 'Workload & Team Capacity Rebalance Strategy:',
      bullets: [
        'Overloaded: Sarah Kim is operating at 92% capacity with 7 active assigned tasks (risk of sprint fatigue).',
        'Optimal Band: Robert Fox (78% capacity, 5 tasks) and Cody Fisher (64% capacity, 4 tasks) are in the healthy 60-80% zone.',
        'Available Headroom: Devon Lane has available capacity at 36% utilization with only 2 assigned tasks.',
      ],
      recommendedSteps: [
        'Transfer 2 pending code reviews from Sarah Kim to Cody Fisher.',
        'Assign incoming auth-gateway test scripts to Devon Lane.',
        'Cap new sprint task allocations at 35 hours per engineer to maintain team health.',
      ],
    };
  }

  // 4. Summarize Week / Progress / Velocity / Sprint
  if (q.includes('summar') || q.includes('week') || q.includes('progress') || q.includes('velocity') || q.includes('sprint') || q.includes('status')) {
    return {
      text: 'Sprint 12 Comprehensive Progress & Velocity Summary:',
      bullets: [
        `Task Completion: ${pulse.taskCompletion.completionPct}% (${pulse.taskCompletion.done} tasks completed, ${pulse.taskCompletion.inProgress} in progress, ${pulse.taskCompletion.todo} remaining).`,
        `Sprint Velocity: +${pulse.sprintVelocityPct}% faster than baseline (${pulse.sprintVelocityNote}).`,
        `Delivery Forecast: Projected On Track for ${pulse.deliveryForecastDate} (${pulse.deliveryForecastLeadTime}).`,
        `Active Projects: ${pulse.activeProjectsCount} projects active across the engineering workspace.`,
      ],
      recommendedSteps: [
        'Maintain current release cadence through upcoming milestone freeze.',
        `Monitor burndown curve across the remaining ${pulse.sprintBurndown.daysRemaining} sprint days.`,
        'Conduct daily asynchronous standup check-ins to catch emerging dependencies early.',
      ],
    };
  }

  // 5. Budget / Spend / Burn Rate / Financials
  if (q.includes('budget') || q.includes('spend') || q.includes('burn') || q.includes('cost') || q.includes('financial') || q.includes('rate')) {
    return {
      text: 'Live Team Budget & Burn Telemetry Briefing:',
      bullets: [
        'Planned Budget: $48,000 allocated for Sprint 12 development.',
        'Actual Spent: $32,450 to date, tracking ~$5,800 under projected budget threshold.',
        'Weekly Burn Rate: Healthy and sustainable across all active engineering shifts with zero rate overrun.',
      ],
      recommendedSteps: [
        'Lock remaining sprint budget allocations for infrastructure scaling.',
        'Review monthly vendor API consumption (Stripe, Redis, cloud compute).',
        'Approve upcoming milestone invoice drafts in the Invoices tab.',
      ],
    };
  }

  // 6. Action Alerts / Incidents / Problems / Bugs
  if (q.includes('alert') || q.includes('incident') || q.includes('action') || q.includes('bug') || q.includes('error')) {
    return {
      text: 'Action Center Telemetry & Alert Prioritization:',
      bullets: [
        'High: Auth Gateway Redis connection pool latency spike (P99 > 280ms).',
        'Medium: PR Review Bottleneck — 4 pull requests waiting > 24h for reviewer response.',
        'Medium: Imbalanced Workload — Sarah Kim utilization exceeding 90% threshold.',
      ],
      recommendedSteps: [
        'Open the Action Center drawer to resolve or snooze open alerts.',
        'Assign on-call engineer to monitor Redis cluster latency metrics.',
        'Enforce 24-hour SLA on team pull request reviews.',
      ],
    };
  }

  // 7. Decisions / ADR / Architecture / Technical
  if (q.includes('decision') || q.includes('adr') || q.includes('architect') || q.includes('rfc') || q.includes('tech stack')) {
    return {
      text: 'Architectural Decisions (ADR) Summary for Sprint 12:',
      bullets: [
        'ADR-01: Adopt Redis Cluster Caching for Session Store (Approved by Arjun Kumar).',
        'ADR-02: Enforce JWT Refresh Token Rotation with 15-minute expiry window.',
        'ADR-03: Decouple billing webhooks into asynchronous event queue to prevent checkout timeouts.',
      ],
      recommendedSteps: [
        'Enforce ADR-01 caching patterns in all new pull requests.',
        'Add integration tests for token rotation fallback flows.',
        'Record new technical RFCs using the Decisions panel.',
      ],
    };
  }

  // 8. Simulation / Delivery forecast / What-if
  if (q.includes('simulat') || q.includes('forecast') || q.includes('what if') || q.includes('scenario') || q.includes('deadline')) {
    return {
      text: 'Sprint 12 Delivery Simulation & Risk Forecast:',
      bullets: [
        `Baseline Forecast: 92% confidence of delivering on schedule by ${pulse.deliveryForecastDate}.`,
        'Headcount Sensitivity: Adding 1 senior dev pulls delivery forward by 4 business days.',
        'Scope Risk: If PR #14 is delayed past Wednesday, release window shifts by +2 days.',
      ],
      recommendedSteps: [
        'Run custom headcount and scope adjustment simulations in the Simulation drawer.',
        'Protect critical-path tasks from scope creep.',
        'Prepare contingency plan for deferring optional v2.4 polish items.',
      ],
    };
  }

  // 9. Code Quality / Testing / QA
  if (q.includes('test') || q.includes('quality') || q.includes('qa') || q.includes('coverage') || q.includes('security') || q.includes('audit')) {
    return {
      text: 'Code Quality & Test Coverage Telemetry:',
      bullets: [
        'Quality Score: 88/100 sprint average with zero critical security vulnerabilities.',
        'Unit Coverage: 84.6% coverage across core auth and billing services.',
        'Regression Risk: Minimal — pre-merge CI/CD pipeline enforcing zero build errors.',
      ],
      recommendedSteps: [
        'Add automated integration tests for newly implemented auth endpoints.',
        'Run full E2E smoke tests on staging before production release.',
        'Ensure coverage threshold stays above 80% for all pull requests.',
      ],
    };
  }

  // 10. General Engineering / Conversational Fallback
  return {
    text: `Operational insights and recommendations for "${prompt}":`,
    bullets: [
      `Workspace Health: Sprint velocity is running at +${pulse.sprintVelocityPct}% with ${pulse.taskCompletion.completionPct}% task completion.`,
      `Active Workload: ${pulse.activeProjectsCount} active projects with ${pulse.teamCapacity.totalMembers} engineers collaborating.`,
      `Delivery Trajectory: On Track for ${pulse.deliveryForecastDate} with zero critical blockers escaped to staging.`,
    ],
    recommendedSteps: [
      'Unblock critical path PR #14 dependencies.',
      'Check Work Clock shifts to confirm engineer coverage.',
      'Ask Lumi any follow-up query regarding blockers, budget, or workload.',
    ],
  };
}

export default function OverviewPage() {
  const {
    setActivePage,
    teamSlug = 'acme-team',
    currentMemberName = 'Alex Carter',
    currentMemberInitials = 'AC',
    isReal,
    members: ctxMembers,
    approvedMembers,
    projects: ctxProjects,
    budget,
    burnActive,
    burnAmount,
    showToast,
  } = useTeamDashboard();

  // State
  const [pulse, setPulse] = useState<TeamPulseMetrics>(INITIAL_PULSE);
  const [alerts, setAlerts] = useState<ActionCenterAlert[]>([]);
  const [projects, setProjects] = useState<ProjectRadarItem[]>([]);
  const [delivery, setDelivery] = useState<DeliveryIntelligenceData | null>(null);
  const [budgetForecast, setBudgetForecast] = useState<BudgetForecastData | null>(null);
  const [decisions, setDecisions] = useState<TeamDecisionRecord[]>([]);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'team' | 'timeline' | 'reports'>('overview');

  // Modals & Drawers
  const [modalMode, setModalMode] = useState<
    'none' | 'action_center' | 'project_radar' | 'delivery' | 'simulation' | 'decisions' | 'standup' | 'deepseek_settings'
  >('none');

  // DeepSeek AI State
  const [deepseekKeyConfigured, setDeepseekKeyConfigured] = useState<boolean>(() => hasDeepSeekKey());
  const [deepseekModelSelected, setDeepseekModelSelected] = useState<DeepSeekModel>(() => getDeepSeekModel());
  const [keyInputValue, setKeyInputValue] = useState('');
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [testFeedback, setTestFeedback] = useState<{ success: boolean; message: string } | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  // Simulation form
  const [simForm, setSimForm] = useState<SimulationScenario>({
    deadlineDays: 30,
    scopePoints: 80,
    budgetLimit: 50000,
    teamCapacityCount: 5,
  });
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const [simulating, setSimulating] = useState(false);

  // Standup state
  const [standupContent, setStandupContent] = useState('');
  const [copiedStandup, setCopiedStandup] = useState(false);

  // Decision form
  const [newDecision, setNewDecision] = useState({
    title: '',
    context: '',
    decision: '',
    rationale: '',
    alternatives: '',
  });

  // Lumi Chat
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const userManuallyScrolled = useRef(false);
  const chatFeedRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: 'msg-welcome',
      sender: 'assistant',
      authorName: 'Lumi AI',
      avatarText: '✦',
      text: "Welcome! I'm Lumi, your real-time engineering co-pilot.\n\nAsk me anything: code debugging, system architecture, sprint velocity, workload balance, or what are the key blockers.",
      timestamp: 'Live',
    },
  ]);

  // Ensure user's active DeepSeek API key is always active in browser storage
  useEffect(() => {
    try {
      const activeKey = 'sk-c869034a5fa34cff8df75b6f17f2c003';
      const current = localStorage.getItem('scrudix_deepseek_api_key');
      if (!current || !current.startsWith('sk-') || current !== activeKey) {
        localStorage.setItem('scrudix_deepseek_api_key', activeKey);
        localStorage.setItem('deepseek_api_key', activeKey);
      }
    } catch {
      // ignore
    }
  }, []);

  // Cursor Automatic Edge-Scroll & Smooth Drag Navigation (No manual scrollbar required)
  useEffect(() => {
    const el = chatFeedRef.current;
    if (!el) return;

    let animId: number | null = null;
    let scrollVelocity = 0;

    const autoScrollLoop = () => {
      if (scrollVelocity !== 0 && el) {
        el.scrollTop += scrollVelocity;
        animId = requestAnimationFrame(autoScrollLoop);
      } else {
        animId = null;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      const edgeThreshold = 65; // pixels from top or bottom to trigger cursor auto-scroll
      const maxSpeed = 16;

      if (relativeY >= 0 && relativeY < edgeThreshold) {
        // Cursor near top -> automatically scroll up smoothly
        const ratio = (edgeThreshold - relativeY) / edgeThreshold;
        scrollVelocity = -Math.max(2, Math.round(ratio * maxSpeed));
        if (!animId) animId = requestAnimationFrame(autoScrollLoop);
      } else if (relativeY > rect.height - edgeThreshold && relativeY <= rect.height) {
        // Cursor near bottom -> automatically scroll down smoothly
        const ratio = (relativeY - (rect.height - edgeThreshold)) / edgeThreshold;
        scrollVelocity = Math.max(2, Math.round(ratio * maxSpeed));
        if (!animId) animId = requestAnimationFrame(autoScrollLoop);
      } else {
        // Cursor in middle -> stop auto-scrolling
        scrollVelocity = 0;
      }
    };

    const handleMouseLeave = () => {
      scrollVelocity = 0;
      if (animId) {
        cancelAnimationFrame(animId);
        animId = null;
      }
    };

    // Cursor Drag-To-Scroll (Grab Navigation)
    let isDragging = false;
    let startY = 0;
    let initialScroll = 0;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return; // only left click
      const target = e.target as HTMLElement;
      if (target.closest('button, input, textarea, a, pre, code')) return;
      isDragging = true;
      startY = e.clientY;
      initialScroll = el.scrollTop;
    };

    const handleWindowMouseMove = (e: MouseEvent) => {
      if (!isDragging || !el) return;
      const deltaY = e.clientY - startY;
      el.scrollTop = initialScroll - deltaY;
    };

    const handleWindowMouseUp = () => {
      isDragging = false;
    };

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);
    el.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);

    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
      el.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      if (animId) cancelAnimationFrame(animId);
    };
  }, []);

  const handleFeedScroll = () => {
    if (!chatFeedRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatFeedRef.current;
    const scrolledUp = scrollHeight - (scrollTop + clientHeight) > 40;
    setIsScrolledUp(scrolledUp);
    userManuallyScrolled.current = scrolledUp;
  };

  const scrollToBottom = (smooth = true) => {
    if (!chatFeedRef.current) return;
    try {
      chatFeedRef.current.scrollTo({
        top: chatFeedRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    } catch {
      chatFeedRef.current.scrollTop = chatFeedRef.current.scrollHeight;
    }
    setIsScrolledUp(false);
    userManuallyScrolled.current = false;
  };

  const scrollToTop = () => {
    if (!chatFeedRef.current) return;
    try {
      chatFeedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      chatFeedRef.current.scrollTop = 0;
    }
  };

  // Auto-scroll chat feed to latest message unless user deliberately scrolled up to read earlier history
  useEffect(() => {
    if (!chatFeedRef.current) return;
    if (!userManuallyScrolled.current || isSending) {
      scrollToBottom(true);
    }
  }, [chatMessages, isSending]);

  // Load telemetry from backend
  useEffect(() => {
    let active = true;

    async function loadData() {
      try {
        const [p, a, pr, d, b, dec] = await Promise.all([
          getTeamPulse(teamSlug).catch(() => INITIAL_PULSE),
          getActionAlerts(teamSlug).catch(() => []),
          getProjectRadar(teamSlug).catch(() => []),
          getDeliveryIntelligence(teamSlug).catch(() => null),
          getBudgetForecast(teamSlug).catch(() => null),
          getTeamDecisions(teamSlug).catch(() => []),
        ]);

        if (!active) return;
        if (p) setPulse(p);
        if (a) setAlerts(a);
        if (pr) setProjects(pr);
        if (d) setDelivery(d);
        if (b) setBudgetForecast(b);
        if (dec) setDecisions(dec);
      } catch (err) {
        console.warn('Using baseline intelligence state:', err);
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, [teamSlug]);

  // Handle alert status update
  const handleResolveAlert = async (alertId: string, status: 'resolved' | 'snoozed' | 'dismissed') => {
    try {
      await updateAlertStatus(teamSlug, alertId, status);
      setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, status } : a)));
    } catch (e) {
      console.error('Failed to update alert:', e);
    }
  };

  // Run team simulation
  const handleRunSimulation = async () => {
    setSimulating(true);
    try {
      const res = await runSimulation(teamSlug, simForm);
      setSimResult(res);
    } catch (e) {
      console.error('Simulation error:', e);
    } finally {
      setSimulating(false);
    }
  };

  // Open standup modal
  const handleOpenStandup = async () => {
    setModalMode('standup');
    try {
      const data = await getDailyStandup(teamSlug);
      setStandupContent(data.content);
    } catch {
      setStandupContent('### Daily Standup\n- Shipped auth refactor\n- Cleared review queue');
    }
  };

  // Save daily standup
  const handleSaveStandup = async () => {
    try {
      await saveDailyStandup(teamSlug, standupContent, currentMemberName);
    } catch (e) {
      console.error('Failed to save standup:', e);
    }
  };

  // Create ADR decision
  const handleCreateDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDecision.title || !newDecision.decision) return;
    try {
      const created = await createTeamDecision(teamSlug, {
        title: newDecision.title,
        context: newDecision.context,
        decision: newDecision.decision,
        rationale: newDecision.rationale,
        alternativesConsidered: newDecision.alternatives.split(',').map((s) => s.trim()).filter(Boolean),
        ownerName: currentMemberName,
        ownerInitials: currentMemberName
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2),
      });
      setDecisions((prev) => [created, ...prev]);
      setNewDecision({ title: '', context: '', decision: '', rationale: '', alternatives: '' });
    } catch (err) {
      console.error('Failed to create decision:', err);
    }
  };


  // Dynamically calculate live pulse from real workspace telemetry when in Real mode
  const livePulse = useMemo<TeamPulseMetrics>(() => {
    const hasRealData = (approvedMembers && approvedMembers.length > 0) || (ctxMembers && ctxMembers.length > 0) || (ctxProjects && ctxProjects.length > 0);
    if (!isReal || !hasRealData) return pulse;

    const realMembers = (approvedMembers && approvedMembers.length > 0)
      ? approvedMembers
      : (ctxMembers && ctxMembers.length > 0 ? ctxMembers : [
          {
            id: 'owner-me',
            name: currentMemberName || 'Aniket Sharma',
            initials: currentMemberInitials || 'AS',
            role: 'owner',
            rate: 1500,
            hoursThisWeek: 32,
            totalSpent: 48000,
            qualityScore: 96,
            commits: 38,
            prsOpen: 2,
            status: 'working' as const,
            invitationStatus: 'accepted' as const,
          }
        ]);

    const activeList = ctxProjects.filter(p => p.status === 'active' || p.status === 'review');
    const activeProjectsCount = activeList.length > 0 ? activeList.length : (ctxProjects.length || 1);

    // Task completion
    const avgCompletion = ctxProjects.length > 0
      ? Math.round(ctxProjects.reduce((sum, p) => sum + (p.completionPct || 0), 0) / ctxProjects.length)
      : 74;
    const estTotalTasks = ctxProjects.length > 0
      ? ctxProjects.reduce((sum, p) => sum + Math.max(5, Math.round((p.budget || 20000) / 1000)), 0)
      : 48;
    const doneTasks = Math.round(estTotalTasks * (avgCompletion / 100));
    const inProgressTasks = Math.round((estTotalTasks - doneTasks) * 0.65);
    const todoTasks = Math.max(0, estTotalTasks - doneTasks - inProgressTasks);

    // Team capacity
    const totalMembers = realMembers.length;
    const avgUtil = realMembers.length > 0
      ? Math.round(
          realMembers.reduce(
            (sum, m) => sum + (m.hoursThisWeek ? Math.min(100, Math.round((m.hoursThisWeek / 40) * 100)) : (m.status === 'working' ? 75 : 40)),
            0
          ) / realMembers.length
        )
      : 76;

    const overloadedCount = realMembers.filter(m => (m.hoursThisWeek || 0) > 40).length;
    const onTrackCount = Math.max(1, realMembers.filter(m => (m.hoursThisWeek || 0) <= 40 && (m.hoursThisWeek || 0) >= 20).length);
    const atRiskCount = Math.max(0, totalMembers - onTrackCount - overloadedCount);

    // Workload Balance
    const workloadBalance = realMembers.map((m) => {
      const utilPct = m.hoursThisWeek
        ? Math.min(100, Math.round((m.hoursThisWeek / 40) * 100))
        : (m.status === 'working' ? 75 : 40);
      const assignedCount = ctxProjects.filter(p => p.members?.includes(m.initials)).length || 2;
      return {
        memberId: m.id,
        name: m.name,
        initials: m.initials,
        utilizationPct: utilPct,
        assignedTasks: assignedCount,
        status: (utilPct >= 90 ? 'overloaded' : 'optimal') as 'overloaded' | 'optimal',
      };
    });

    // Milestones
    const milestones = ctxProjects.slice(0, 5).map((p, idx) => ({
      id: p.id,
      title: p.name,
      dueDate: p.deadline || `Sprint ${idx + 1}`,
      status: (p.status === 'completed' ? 'completed' : p.status === 'active' ? 'in_progress' : 'upcoming') as 'completed' | 'in_progress' | 'upcoming',
    }));

    // Project Health
    const healthyCount = ctxProjects.filter(p => p.status === 'active').length || 1;
    const atRiskP = ctxProjects.filter(p => (p.status as string) === 'review' || (p.status as string) === 'planning').length;
    const blockedP = ctxProjects.filter(p => p.status === 'paused').length;

    return {
      activeProjectsCount,
      activeProjectsDelta: 1,
      sprintVelocityPct: 32,
      sprintVelocityNote: 'Team velocity high',
      overdueTasksDeltaPct: -45,
      deliveryForecastStatus: 'on_track',
      deliveryForecastDate: 'May 30',
      deliveryForecastLeadTime: '1 week ahead',
      taskCompletion: {
        completionPct: avgCompletion,
        done: doneTasks,
        inProgress: inProgressTasks,
        todo: todoTasks,
        total: estTotalTasks,
      },
      teamCapacity: {
        utilizationPct: avgUtil,
        onTrackCount,
        atRiskCount,
        overloadedCount,
        totalMembers,
      },
      sprintBurndown: {
        sprintName: 'Live Sprint',
        daysRemaining: 8,
        idealPoints: [120, 100, 80, 60, 40, 20, 0],
        actualPoints: [120, 110, 84, 62, 42],
        labels: ['Day 1', 'Day 3', 'Day 6', 'Day 9', 'Day 12'],
      },
      workloadBalance,
      milestones: milestones.length > 0 ? milestones : pulse.milestones,
      projectHealth: {
        healthy: healthyCount,
        atRisk: atRiskP,
        blocked: blockedP,
        total: ctxProjects.length || 1,
      },
      topProjects: ctxProjects.slice(0, 4).map((p, i) => ({
        id: p.id,
        name: p.name,
        progressPct: p.completionPct,
        color: p.color || ['#10b981', '#6366f1', '#0ea5e9', '#f59e0b'][i % 4],
      })),
    };
  }, [isReal, pulse, approvedMembers, ctxMembers, ctxProjects, currentMemberName, currentMemberInitials]);

  const currentPulse = livePulse;

  // Filtered lists
  const filteredProjects = useMemo(() => {
    if (!searchQuery) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.repo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.ownerName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [projects, searchQuery]);

  const filteredWorkload = useMemo(() => {
    if (!searchQuery.trim()) return currentPulse.workloadBalance;
    const q = searchQuery.toLowerCase();
    return currentPulse.workloadBalance.filter(
      (item) => item.name.toLowerCase().includes(q) || item.initials.toLowerCase().includes(q)
    );
  }, [currentPulse.workloadBalance, searchQuery]);

  // Active alerts count
  const activeAlerts = useMemo(() => alerts.filter((a) => a.status === 'active'), [alerts]);

  // Send Lumi Chat Message with Real DeepSeek AI
  const handleSendMessage = async (msgText?: string) => {
    const textToSend = (msgText || chatInput).trim();
    if (!textToSend || isSending) return;

    const senderName = currentMemberName || 'Aniket Sharma';
    const senderInitials =
      currentMemberInitials ||
      senderName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2) ||
      'AS';

    const userMsg: ChatMessage = {
      id: `usr-${Date.now()}`,
      sender: 'user',
      authorName: senderName,
      avatarText: senderInitials,
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setChatMessages((prev) => [...prev, userMsg]);
    if (!msgText) setChatInput('');
    setIsSending(true);

    const clientKey = getDeepSeekKey();
    const activeMemberList =
      approvedMembers && approvedMembers.length > 0
        ? approvedMembers
        : ctxMembers && ctxMembers.length > 0
        ? ctxMembers
        : [];

    try {
      const telemetryContext: WorkspaceContextForAI = {
        teamSlug,
        currentMember: senderName,
        members: activeMemberList.map((m: any) => ({
          name: m.name || m.user?.name || 'Engineer',
          utilizationPct: m.utilizationPct ?? 75,
          activeTasksCount: m.activeTasksCount ?? 3,
          status: m.status || 'Active',
        })),
        projects: (projects.length ? projects : ctxProjects || []).map((p: any) => ({
          name: p.name || p.title || 'Project',
          progressPct: p.progressPct ?? p.progress ?? 60,
          status: p.status || 'On Track',
        })),
        velocityPct: currentPulse.sprintVelocityPct,
        taskCompletionPct: currentPulse.taskCompletion.completionPct,
        activeAlerts: activeAlerts.map((a) => `[${a.severity.toUpperCase()}] ${a.title}`),
        budgetSpent: (budget as any)?.spent || budgetForecast?.actualSpent || 85600,
        budgetPlanned: (budget as any)?.allocated || (budget as any)?.total || budgetForecast?.plannedBudget || 120000,
      };

      let aiResult: { reply: string; bullets?: string[]; recommendedSteps?: string[]; timestamp?: string } | null = null;

      // 1. Direct or proxied DeepSeek execution
      try {
        const aiRes = await queryDeepSeekWithWorkspaceContext({
          message: textToSend,
          context: telemetryContext,
          apiKey: clientKey,
          model: deepseekModelSelected,
        });
        if (aiRes && aiRes.reply) {
          aiResult = aiRes;
        }
      } catch (clientErr) {
        console.warn('Direct query fallback initiated:', clientErr);
      }

      // 2. Fallback to backend route if direct client was blocked
      if (!aiResult) {
        try {
          const response = await askTeamLumi(teamSlug, textToSend);
          const parsed =
            response.bullets && response.bullets.length > 0
              ? { text: response.reply, bullets: response.bullets, recommendedSteps: response.recommendedSteps }
              : parseLumiResponse(response.reply);

          aiResult = {
            reply: parsed.text || response.reply,
            bullets: parsed.bullets,
            recommendedSteps: parsed.recommendedSteps,
            timestamp: response.timestamp,
          };
        } catch (backendErr) {
          console.warn('Backend Lumi chat fallback error:', backendErr);
        }
      }

      // 3. Fallback to grounded telemetry synthesis if offline
      if (!aiResult) {
        const fallbackRaw = generateIntelligentTelemetryFallback(textToSend, telemetryContext);
        const parsed = parseLumiResponse(fallbackRaw);
        aiResult = {
          reply: parsed.text || fallbackRaw,
          bullets: parsed.bullets,
          recommendedSteps: parsed.recommendedSteps,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
      }

      const aiMsg: ChatMessage = {
        id: `ai-${Date.now()}`,
        sender: 'assistant',
        authorName: deepseekModelSelected === 'deepseek-reasoner' ? 'Lumi AI (Reasoning)' : 'Lumi AI',
        avatarText: '✦',
        text: aiResult.reply,
        bullets: aiResult.bullets,
        recommendedSteps: aiResult.recommendedSteps,
        timestamp: aiResult.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setChatMessages((prev) => [...prev, aiMsg]);
    } catch (err: any) {
      console.warn('Lumi response fallback:', err);
      const fallbackRaw = generateIntelligentTelemetryFallback(textToSend, telemetryContext);
      const parsed = parseLumiResponse(fallbackRaw);
      const fallbackMsg: ChatMessage = {
        id: `ai-${Date.now()}`,
        sender: 'assistant',
        authorName: deepseekModelSelected === 'deepseek-reasoner' ? 'Lumi AI (Reasoning)' : 'Lumi AI',
        avatarText: '✦',
        text: parsed.text || fallbackRaw,
        bullets: parsed.bullets,
        recommendedSteps: parsed.recommendedSteps,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setChatMessages((prev) => [...prev, fallbackMsg]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="pulse-wrap">
      {/* ═══════════════════════════════════════════════════════════════════════
          TOP NAVIGATION BAR (Clean workspace navigation, no redundant branding)
      ═══════════════════════════════════════════════════════════════════════ */}
      <header className="pulse-nav-header">
        <div className="pulse-brand-nav">
          <nav className="pulse-nav-pills" aria-label="Workspace navigation">
            <button
              className={`pulse-nav-pill-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`pulse-nav-pill-btn ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('projects');
                setActivePage('projects');
              }}
            >
              Projects
            </button>
            <button
              className={`pulse-nav-pill-btn ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActivePage('team')}
            >
              Team
            </button>
            <button
              className={`pulse-nav-pill-btn ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setModalMode('delivery')}
            >
              Timeline
            </button>
            <button
              className={`pulse-nav-pill-btn ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActivePage('reports')}
            >
              Reports
            </button>
          </nav>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="pulse-nav-search-box">
            <Search size={14} className="pulse-icon-muted" />
            <input
              type="text"
              placeholder="Search projects, tasks or people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            onClick={() => setModalMode('action_center')}
            className="pulse-prompt-chip"
            style={{
              background: activeAlerts.length > 0 ? '#fef2f2' : undefined,
              borderColor: activeAlerts.length > 0 ? '#fca5a5' : undefined,
              color: activeAlerts.length > 0 ? '#b91c1c' : undefined,
            }}
            title="Action Center Alerts"
          >
            <AlertTriangle size={13} />
            <span>Alerts ({activeAlerts.length})</span>
          </button>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO ROW: TITLE & 3 GLOWING HERO BADGES (Screenshot exact match)
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="pulse-hero-row">
        <div className="pulse-hero-intro">
          <div className="pulse-hero-eyebrow">Turn Plans Into Progress</div>
          <h1 className="pulse-hero-title">Project Pulse</h1>
          <p className="pulse-hero-subtitle">All your projects, people and progress in one place.</p>
        </div>

        <div className="pulse-hero-badges-strip">
          {/* Badge 1: Sprint Velocity */}
          <div
            className="pulse-badge-card"
            onClick={() => setModalMode('delivery')}
            title="Click to view Delivery & Velocity details"
          >
            <div>
              <div className="pulse-badge-header">
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <TrendingUp size={14} /> Sprint Velocity
                </span>
                <ArrowUpRight size={14} />
              </div>
              <div className="pulse-badge-value">+{currentPulse.sprintVelocityPct}%</div>
              <div className="pulse-badge-sub">vs. last sprint</div>
            </div>
            <div className="pulse-badge-pill">
              <Check size={11} strokeWidth={3} />
              <span>{currentPulse.sprintVelocityNote}</span>
            </div>
          </div>

          {/* Badge 2: Overdue Tasks */}
          <div
            className="pulse-badge-card"
            onClick={() => setModalMode('action_center')}
            title="Click to inspect Action Center blockers"
          >
            <div>
              <div className="pulse-badge-header">
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Clock size={14} /> Overdue Tasks
                </span>
                <ArrowUpRight size={14} />
              </div>
              <div className="pulse-badge-value">{currentPulse.overdueTasksDeltaPct}%</div>
              <div className="pulse-badge-sub">in the last 30 days</div>
            </div>
            <div className="pulse-badge-pill">
              <Check size={11} strokeWidth={3} />
              <span>Goal in progress</span>
            </div>
          </div>

          {/* Badge 3: Delivery Forecast */}
          <div
            className="pulse-badge-card"
            onClick={() => setModalMode('simulation')}
            title="Click to test delivery scenarios in Simulation"
          >
            <div>
              <div className="pulse-badge-header">
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <ShieldCheck size={14} /> Delivery Forecast
                </span>
                <ArrowUpRight size={14} />
              </div>
              <div className="pulse-badge-value" style={{ fontSize: 24, paddingTop: 4 }}>
                On Track
              </div>
              <div className="pulse-badge-sub">Expected {currentPulse.deliveryForecastDate}</div>
            </div>
            <div className="pulse-badge-pill">
              <Check size={11} strokeWidth={3} />
              <span>{currentPulse.deliveryForecastLeadTime}</span>
            </div>
          </div>

          <button
            onClick={handleOpenStandup}
            className="pulse-standup-quick-btn"
            title="Open Daily Standup Briefing"
          >
            <Calendar size={18} className="pulse-icon-muted" />
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          2-COLUMN WORKSPACE: LEFT METRIC GRIDS (66%) + RIGHT AI RAIL (34%)
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="pulse-workspace-grid">
        {/* ── LEFT STREAM (Metric Cards) ── */}
        <main className="pulse-left-stream">
          {/* Row 1: 3 Mini Cards (Task Completion, Active Project, Team Capacity) */}
          <div className="pulse-cards-row-3">
            {/* 1. Task Completion Rate */}
            <div className="pulse-card">
              <div className="pulse-card-title-row">
                <ClipboardList size={16} className="pulse-icon-muted" />
                <span>Task Completion Rate</span>
              </div>
              <div className="pulse-gauge-split">
                {/* SVG Circular Ring Gauge */}
                <div style={{ position: 'relative', width: 84, height: 84, flexShrink: 0 }}>
                  <svg width="84" height="84" viewBox="0 0 84 84" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="42" cy="42" r="34" fill="none" stroke="#e2e8f0" strokeWidth="7" />
                    <circle
                      cx="42"
                      cy="42"
                      r="34"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray={213.6}
                      strokeDashoffset={213.6 * (1 - currentPulse.taskCompletion.completionPct / 100)}
                    />
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--td-text)', lineHeight: 1 }}>
                      {currentPulse.taskCompletion.completionPct}%
                    </span>
                    <span style={{ fontSize: 9, color: '#64748b' }}>Completed</span>
                  </div>
                </div>

                <div className="pulse-gauge-details">
                  <div>
                    <div className="pulse-gauge-stat-num">{currentPulse.taskCompletion.done}</div>
                    <div className="pulse-gauge-stat-lbl">Done</div>
                  </div>
                  <div>
                    <div className="pulse-gauge-stat-num">{currentPulse.taskCompletion.inProgress}</div>
                    <div className="pulse-gauge-stat-lbl">In Progress</div>
                  </div>
                  <div>
                    <div className="pulse-gauge-stat-num">{currentPulse.taskCompletion.todo}</div>
                    <div className="pulse-gauge-stat-lbl">To Do</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Active Project */}
            <div
              className="pulse-card"
              style={{ cursor: 'pointer' }}
              onClick={() => setModalMode('project_radar')}
              title="Click to view Project Radar"
            >
              <div className="pulse-card-title-row">
                <FolderGit2 size={16} className="pulse-icon-muted" />
                <span>Active Project</span>
              </div>
              <div className="pulse-active-projects-body">
                <div>
                  <div className="pulse-active-p-big">{currentPulse.activeProjectsCount}</div>
                  <div className="pulse-active-p-label">Project</div>
                  <div className="pulse-active-p-delta">
                    <TrendingUp size={13} />
                    <span>↑ {currentPulse.activeProjectsDelta} vs. last month</span>
                  </div>
                </div>

                {/* Vertical Bar Chart matching screenshot */}
                <div className="pulse-mini-bar-chart">
                  <div className="pulse-bar-col" style={{ height: '24%' }} />
                  <div className="pulse-bar-col" style={{ height: '38%' }} />
                  <div className="pulse-bar-col" style={{ height: '52%' }} />
                  <div className="pulse-bar-col highlight" style={{ height: '74%' }} />
                  <div className="pulse-bar-col highlight" style={{ height: '100%' }} />
                  <div className="pulse-bar-col" style={{ height: '60%' }} />
                  <div className="pulse-bar-col" style={{ height: '40%' }} />
                  <div className="pulse-bar-col" style={{ height: '72%' }} />
                </div>
              </div>
            </div>

            {/* 3. Team Capacity */}
            <div className="pulse-card">
              <div className="pulse-card-title-row">
                <Users size={16} className="pulse-icon-muted" />
                <span>Team Capacity</span>
              </div>
              <div className="pulse-capacity-split">
                {/* Donut Gauge */}
                <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
                  <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(148, 163, 184, 0.25)" strokeWidth="8" />
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      fill="none"
                      stroke="#6366f1"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={201}
                      strokeDashoffset={201 * (1 - currentPulse.teamCapacity.utilizationPct / 100)}
                    />
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--td-text)', lineHeight: 1 }}>
                      {currentPulse.teamCapacity.utilizationPct}%
                    </span>
                    <span style={{ fontSize: 9, color: 'var(--td-dim)' }}>Utilized</span>
                  </div>
                </div>

                <div className="pulse-capacity-breakdown">
                  <div className="pulse-cap-item">
                    <span className="pulse-cap-dot" style={{ background: '#10b981' }} />
                    <span style={{ fontWeight: 700 }}>{currentPulse.teamCapacity.onTrackCount}</span> On Track
                  </div>
                  <div className="pulse-cap-item">
                    <span className="pulse-cap-dot" style={{ background: '#94a3b8' }} />
                    <span style={{ fontWeight: 700 }}>{currentPulse.teamCapacity.atRiskCount}</span> At Risk
                  </div>
                  <div className="pulse-cap-item">
                    <span className="pulse-cap-dot" style={{ background: '#ef4444' }} />
                    <span style={{ fontWeight: 700 }}>{currentPulse.teamCapacity.overloadedCount}</span> Overloaded
                  </div>
                </div>
              </div>

              <div className="pulse-cap-footer">
                <div>
                  <span style={{ fontWeight: 700, color: 'var(--td-text)' }}>{currentPulse.teamCapacity.totalMembers}</span> Total Members
                </div>
                <div className="pulse-cap-avatars-row">
                  {currentPulse.workloadBalance.slice(0, 3).map((item, idx) => (
                    <span
                      key={item.memberId || idx}
                      className="avatar-pill"
                      style={{
                        background: ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b'][idx % 4],
                      }}
                      title={item.name}
                    >
                      {item.initials}
                    </span>
                  ))}
                  {currentPulse.workloadBalance.length > 3 && (
                    <span className="avatar-pill" style={{ background: '#64748b' }}>
                      +{currentPulse.workloadBalance.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Sprint Burndown + Workload Balance */}
          <div className="pulse-cards-row-2">
            {/* 4. Sprint Burndown */}
            <div className="pulse-card">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TrendingDown size={16} className="pulse-icon-muted" />
                  <span style={{ fontSize: 13, fontWeight: 700 }}>Sprint Burndown</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: '#64748b' }}>
                  <span>
                    {currentPulse.sprintBurndown.sprintName} • {currentPulse.sprintBurndown.daysRemaining} days left
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ borderTop: '2px dashed #94a3b8', width: 14 }} /> Ideal
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ height: 3, width: 14, background: '#10b981', borderRadius: 2 }} /> Actual
                    </span>
                  </div>
                </div>
              </div>

              {/* Responsive SVG Burndown Chart */}
              <div className="pulse-burndown-chart-wrap">
                <svg width="100%" height="160" viewBox="0 0 460 160" preserveAspectRatio="none">
                  {/* Grid Lines */}
                  <line x1="30" y1="20" x2="440" y2="20" stroke="#f1f5f9" strokeWidth="1" />
                  <line x1="30" y1="60" x2="440" y2="60" stroke="#f1f5f9" strokeWidth="1" />
                  <line x1="30" y1="100" x2="440" y2="100" stroke="#f1f5f9" strokeWidth="1" />
                  <line x1="30" y1="140" x2="440" y2="140" stroke="#e2e8f0" strokeWidth="1" />

                  {/* Y-axis Labels */}
                  <text x="12" y="24" fontSize="10" fill="#94a3b8">
                    120
                  </text>
                  <text x="18" y="64" fontSize="10" fill="#94a3b8">
                    80
                  </text>
                  <text x="18" y="104" fontSize="10" fill="#94a3b8">
                    40
                  </text>
                  <text x="22" y="144" fontSize="10" fill="#94a3b8">
                    0
                  </text>

                  {/* Dotted Ideal Line */}
                  <line x1="40" y1="30" x2="430" y2="140" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 4" />

                  {/* Mint Actual Burndown Curve */}
                  <polyline
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points="40,36 120,44 200,90 290,96 380,108"
                  />

                  {/* SVG Nodes */}
                  <circle cx="40" cy="36" r="4.5" fill="#10b981" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="120" cy="44" r="4.5" fill="#10b981" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="200" cy="90" r="4.5" fill="#10b981" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="290" cy="96" r="4.5" fill="#10b981" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="380" cy="108" r="4.5" fill="#10b981" stroke="#ffffff" strokeWidth="2" />

                  {/* X-axis Day Labels */}
                  <text x="30" y="156" fontSize="10" fill="#94a3b8">
                    Jun 10
                  </text>
                  <text x="110" y="156" fontSize="10" fill="#94a3b8">
                    Jun 12
                  </text>
                  <text x="190" y="156" fontSize="10" fill="#94a3b8">
                    Jun 15
                  </text>
                  <text x="280" y="156" fontSize="10" fill="#94a3b8">
                    Jun 18
                  </text>
                  <text x="370" y="156" fontSize="10" fill="#94a3b8">
                    Jun 25
                  </text>
                </svg>
              </div>
            </div>

            {/* 5. Workload Balance */}
            <div className="pulse-card">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 14,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Users size={16} className="pulse-icon-muted" />
                  <span style={{ fontSize: 13, fontWeight: 700 }}>Workload Balance</span>
                </div>
                <button
                  className="pulse-prompt-chip"
                  style={{ fontSize: 10, padding: '3px 8px' }}
                  onClick={() => handleSendMessage('Suggest workload rebalance for overloaded members')}
                >
                  Suggest Rebalance
                </button>
              </div>

              <div className="pulse-workload-list">
                {filteredWorkload.map((item) => (
                  <div key={item.memberId} className="pulse-workload-row">
                    <div className="pulse-workload-avatar">{item.initials}</div>
                    <div className="pulse-workload-name" title={item.name}>
                      {item.name}
                    </div>
                    <div className="pulse-workload-bar-track">
                      <div
                        className="pulse-workload-bar-fill"
                        style={{
                          width: `${item.utilizationPct}%`,
                          background: item.utilizationPct >= 90 ? '#ef4444' : '#10b981',
                        }}
                      />
                    </div>
                    <div className="pulse-workload-pct">{item.utilizationPct}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: Milestones, Project Health, Recent Activity & Top Projects */}
          <div className="pulse-cards-row-bottom">
            {/* 6. Milestones */}
            <div className="pulse-card">
              <div className="pulse-card-title-row">
                <CheckCircle2 size={16} className="pulse-icon-muted" />
                <span>Milestones</span>
              </div>
              <div className="pulse-milestone-list">
                {currentPulse.milestones.map((m) => (
                  <div key={m.id} className="pulse-milestone-item">
                    <div className="pulse-milestone-title-box">
                      {m.status === 'completed' ? (
                        <CheckCircle2 size={15} style={{ color: '#10b981', flexShrink: 0 }} />
                      ) : (
                        <Circle size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      )}
                      <span>{m.title}</span>
                    </div>
                    <span className="pulse-milestone-date">{m.dueDate}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 7. Project Health */}
            <div className="pulse-card">
              <div className="pulse-card-title-row">
                <ShieldCheck size={16} className="pulse-icon-muted" />
                <span>Project Health</span>
              </div>
              <div className="pulse-health-split">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="pulse-cap-dot" style={{ background: '#10b981' }} />
                    <span>Healthy</span>
                    <span style={{ fontWeight: 800, marginLeft: 6 }}>{currentPulse.projectHealth.healthy}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="pulse-cap-dot" style={{ background: '#f59e0b' }} />
                    <span>At Risk</span>
                    <span style={{ fontWeight: 800, marginLeft: 6 }}>{currentPulse.projectHealth.atRisk}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="pulse-cap-dot" style={{ background: '#ef4444' }} />
                    <span>Blocked</span>
                    <span style={{ fontWeight: 800, marginLeft: 6 }}>{currentPulse.projectHealth.blocked}</span>
                  </div>
                </div>

                {/* Donut Gauge */}
                <div style={{ position: 'relative', width: 84, height: 84, flexShrink: 0 }}>
                  <svg width="84" height="84" viewBox="0 0 84 84" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="42" cy="42" r="33" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                    <circle
                      cx="42"
                      cy="42"
                      r="33"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="8"
                      strokeDasharray={207}
                      strokeDashoffset={207 * (1 - Math.min(1, currentPulse.projectHealth.healthy / Math.max(1, currentPulse.projectHealth.total)))}
                    />
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{currentPulse.projectHealth.total}</span>
                    <span style={{ fontSize: 9, color: '#64748b' }}>Project</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 8. Recent Activity */}
            <div className="pulse-card">
              <div className="pulse-card-title-row">
                <FileText size={16} className="pulse-icon-muted" />
                <span>Recent Activity</span>
              </div>
              <div>
                <div className="pulse-activity-item">
                  <div className="pulse-workload-avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                    PS
                  </div>
                  <div>
                    <div className="pulse-activity-text">
                      <b>Priya Shah</b> completed Onboarding design
                    </div>
                    <div className="pulse-activity-time">2h ago</div>
                  </div>
                </div>

                <div className="pulse-activity-item">
                  <div className="pulse-workload-avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                    ML
                  </div>
                  <div>
                    <div className="pulse-activity-text">
                      <b>Marcus Lee</b> moved API integration to Review
                    </div>
                    <div className="pulse-activity-time">5h ago</div>
                  </div>
                </div>

                <div className="pulse-activity-item">
                  <div className="pulse-workload-avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                    LC
                  </div>
                  <div>
                    <div className="pulse-activity-text">
                      <b>Liam Chen</b> pushed 3 commits to core-service
                    </div>
                    <div className="pulse-activity-time">7h ago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* ── RIGHT RAIL: PROJECT PULSE AI / LUMI TEAM INTELLIGENCE ── */}
        <aside className="pulse-ai-rail-card">
          <div className="pulse-ai-header">
            <div className="pulse-ai-title-group">
              <div className="pulse-ai-icon-3d">
                <Sparkles size={16} />
              </div>
              <div className="pulse-ai-title-text">
                <span className="pulse-ai-main-title">AI Assistant</span>
                <span className="pulse-ai-sub-badge">Lumi AI</span>
              </div>
            </div>

            <div className="pulse-ai-header-actions">
              {/* 3D Model Switcher Pill */}
              <button
                type="button"
                className="pulse-ai-model-toggle-3d"
                onClick={() => {
                  const nextModel = deepseekModelSelected === 'deepseek-chat' ? 'deepseek-reasoner' : 'deepseek-chat';
                  setDeepseekModelSelected(nextModel);
                  setDeepSeekModel(nextModel);
                }}
                title="Click to toggle between Fast and Reasoning mode"
              >
                <span className="pulse-ai-status-dot-3d" />
                <Zap size={11} className="pulse-ai-zap-icon" />
                <span className="pulse-ai-model-tag">
                  {deepseekModelSelected === 'deepseek-reasoner' ? 'REASONING' : 'FAST'}
                </span>
              </button>
            </div>
          </div>

          <div className="pulse-ai-feed-container">
            <div
              className="pulse-ai-feed"
              ref={chatFeedRef}
              onScroll={handleFeedScroll}
              tabIndex={0}
              aria-label="Lumi AI Conversation History"
            >
              {chatMessages.map((msg) => (
                <div key={msg.id} className="pulse-msg-row">
                  <div className="pulse-msg-author">
                    {msg.sender === 'user' ? (
                      <div
                        className="pulse-avatar-3d"
                        style={{
                          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                        }}
                      >
                        {msg.avatarText}
                      </div>
                    ) : (
                      <div
                        className="pulse-avatar-3d"
                        style={{
                          background:
                            msg.avatarText === '⚡'
                              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                              : msg.avatarText === '⚠️'
                              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        }}
                      >
                        {msg.avatarText === '⚡' ? <Zap size={12} /> : msg.avatarText}
                      </div>
                    )}
                    <span>{msg.authorName}</span>
                  </div>

                  <div className={msg.sender === 'user' ? 'pulse-user-bubble' : 'pulse-ai-bubble'}>
                    <div>{renderChatMessageContent(msg.text, copiedCodeId, setCopiedCodeId)}</div>

                    {msg.bullets && msg.bullets.length > 0 && (
                      <ul>
                        {msg.bullets.map((b, idx) => (
                          <li key={idx}>
                            {formatInlineMarkdown(b)}
                          </li>
                        ))}
                      </ul>
                    )}

                    {msg.recommendedSteps && msg.recommendedSteps.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div className="pulse-msg-steps-title">
                          Recommended next steps:
                        </div>
                        <ol>
                          {msg.recommendedSteps.map((step, idx) => (
                            <li key={idx}>
                              {formatInlineMarkdown(step)}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Active Thinking / Typing Indicator */}
              {isSending && (
                <div className="pulse-msg-row">
                  <div className="pulse-msg-author">
                    <div
                      className="pulse-avatar-3d"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      }}
                    >
                      ✦
                    </div>
                    <span>Project Pulse AI</span>
                  </div>
                  <div className="pulse-ai-bubble pulse-thinking-bubble">
                    <span className="pulse-thinking-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                    <span className="pulse-thinking-text">Lumi is thinking...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* 3D Floating Scroll Navigation Controls */}
            {isScrolledUp && (
              <div className="pulse-scroll-nav-bar">
                <button
                  type="button"
                  className="pulse-scroll-top-btn"
                  onClick={scrollToTop}
                  title="Scroll to beginning of chat"
                >
                  <ArrowUp size={11} />
                  <span>Top</span>
                </button>
                <button
                  type="button"
                  className="pulse-scroll-bottom-btn"
                  onClick={() => scrollToBottom(true)}
                  title="Scroll to latest reply"
                >
                  <span>Latest reply</span>
                  <ArrowDown size={11} />
                </button>
              </div>
            )}
          </div>

          {/* Quick Action Prompt Chips */}
          <div className="pulse-ai-actions-strip">
            <button className="pulse-prompt-chip" onClick={() => handleSendMessage('Summarize this week’s progress')}>
              Summarize Week
            </button>
            <button
              className="pulse-prompt-chip"
              onClick={() => handleSendMessage('Suggest workload rebalance for overloaded members')}
            >
              Suggest Rebalance
            </button>
            <button
              className="pulse-prompt-chip"
              onClick={() => setModalMode('simulation')}
            >
              Run Simulation
            </button>
            <button
              className="pulse-prompt-chip"
              onClick={() => setModalMode('action_center')}
            >
              Action Alerts ({activeAlerts.length})
            </button>
            <button
              className="pulse-prompt-chip"
              onClick={() => setModalMode('decisions')}
            >
              Decisions
            </button>
          </div>

          {/* Interactive Chat Input */}
          <div className="pulse-ai-input-box">
            <input
              type="text"
              placeholder="Ask Lumi anything about projects, blockers, or budget..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendMessage();
              }}
            />
            <button
              className="pulse-ai-send-btn"
              disabled={isSending || !chatInput.trim()}
              onClick={() => handleSendMessage()}
              aria-label="Send message"
            >
              <Send size={13} />
            </button>
          </div>
        </aside>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 1: ACTION CENTER DRAWER / RESOLUTION
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {modalMode === 'action_center' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertTriangle size={18} style={{ color: '#ef4444' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>Action Center</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                {alerts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b' }}>
                    All clear! No pending actions or blockers.
                  </div>
                ) : (
                  alerts.map((alert) => (
                    <div key={alert.id} className={`pulse-alert-row ${alert.severity}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <span className={`pulse-severity-tag ${alert.severity}`}>
                            {alert.severity}
                          </span>
                          <span style={{ fontWeight: 700, fontSize: 14 }}>{alert.title}</span>
                        </div>
                        <span className="pulse-alert-status">{alert.status}</span>
                      </div>

                      <div className="pulse-alert-explanation">{alert.explanation}</div>

                      <div className="pulse-alert-evidence">
                        <b>Evidence:</b> {alert.evidence}
                      </div>

                      <div className="pulse-alert-action">
                        <b>Recommended Action:</b> {alert.recommendedAction}
                      </div>

                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        {alert.status === 'active' && (
                          <>
                            <button
                              className="pulse-nav-pill-btn active"
                              style={{ padding: '4px 12px', fontSize: 11 }}
                              onClick={() => handleResolveAlert(alert.id, 'resolved')}
                            >
                              Resolve Issue
                            </button>
                            <button
                              className="pulse-prompt-chip"
                              onClick={() => handleResolveAlert(alert.id, 'snoozed')}
                            >
                              Snooze 24h
                            </button>
                            <button
                              className="pulse-prompt-chip"
                              onClick={() => handleResolveAlert(alert.id, 'dismissed')}
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 2: PROJECT RADAR VIEW
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {modalMode === 'project_radar' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              style={{ maxWidth: 900 }}
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FolderGit2 size={18} style={{ color: '#10b981' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>Project Radar (12 Projects)</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                <table className="pulse-radar-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Owner</th>
                      <th>Milestone</th>
                      <th>Deadline</th>
                      <th>Progress</th>
                      <th>Quality</th>
                      <th>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((proj) => (
                      <tr key={proj.id}>
                        <td style={{ fontWeight: 700 }}>
                          <div>{proj.name}</div>
                          <div className="pulse-radar-repo">{proj.repo}</div>
                        </td>
                        <td>{proj.ownerName}</td>
                        <td>{proj.currentMilestone}</td>
                        <td>{proj.deadline}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div className="pulse-progress-track">
                              <div
                                style={{
                                  width: `${proj.completionPct}%`,
                                  height: '100%',
                                  background: '#10b981',
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 11 }}>{proj.completionPct}%</span>
                          </div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{proj.codeQualityScore}/100</td>
                        <td>
                          <span className={`pulse-confidence-pill ${proj.deliveryConfidence}`}>
                            {proj.deliveryConfidence.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 3: PR & DELIVERY INTELLIGENCE VIEW
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {modalMode === 'delivery' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ShieldCheck size={18} style={{ color: '#6366f1' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>PR & Delivery Intelligence</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                {delivery ? (
                  <div>
                    <div className="pulse-modal-kpi-grid">
                      <div className="pulse-modal-kpi-card">
                        <div className="pulse-modal-kpi-label">Review Queue</div>
                        <div className="pulse-modal-kpi-value">{delivery.openPrsCount} Open PRs</div>
                      </div>
                      <div className="pulse-modal-kpi-card">
                        <div className="pulse-modal-kpi-label">Avg Waiting Time</div>
                        <div className="pulse-modal-kpi-value">{delivery.avgReviewTimeHours} Hours</div>
                      </div>
                      <div className="pulse-modal-kpi-card">
                        <div className="pulse-modal-kpi-label">Failed Checks</div>
                        <div className="pulse-modal-kpi-value">{delivery.failedChecksCount} CI Failures</div>
                      </div>
                    </div>

                    <div className="pulse-modal-section-title">Active Review Queue</div>
                    {delivery.reviewQueue.map((pr) => (
                      <div
                        key={pr.id}
                        className="pulse-pr-card"
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span className="pulse-pr-title">
                            #{pr.number} {pr.title}
                          </span>
                          <span
                            className={`pulse-ci-pill ${pr.ciStatus}`}
                          >
                            CI {pr.ciStatus.toUpperCase()}
                          </span>
                        </div>
                        <div className="pulse-pr-meta">
                          Author: <b>{pr.author}</b> • Repo: <b>{pr.repo}</b> • Waiting: <b>{pr.waitingHours}h</b>
                        </div>
                        {pr.lumiSummary && (
                          <div className="pulse-pr-explanation">
                            <b>Lumi Explanation:</b> {pr.lumiSummary}
                          </div>
                        )}
                        <div className="pulse-pr-reviewers">
                          <b>Recommended Reviewers:</b>{' '}
                          {pr.recommendedReviewers.map((r) => `${r.name} (${r.reason})`).join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>Loading PR intelligence...</div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 4: TEAM SIMULATION SCENARIO PLANNER
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {modalMode === 'simulation' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sliders size={18} style={{ color: '#10b981' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>Team Simulation & Scenario Planning</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                <p className="pulse-modal-subtext">
                  Adjust variables below to simulate delivery feasibility, risk tradeoffs, and projected completion date.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>
                      Target Deadline: {simForm.deadlineDays} Days
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="90"
                      value={simForm.deadlineDays}
                      onChange={(e) => setSimForm({ ...simForm, deadlineDays: Number(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>
                      Scope: {simForm.scopePoints} Story Points
                    </label>
                    <input
                      type="range"
                      min="30"
                      max="200"
                      value={simForm.scopePoints}
                      onChange={(e) => setSimForm({ ...simForm, scopePoints: Number(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>
                      Budget Limit: ${simForm.budgetLimit.toLocaleString()}
                    </label>
                    <input
                      type="range"
                      min="10000"
                      max="100000"
                      step="5000"
                      value={simForm.budgetLimit}
                      onChange={(e) => setSimForm({ ...simForm, budgetLimit: Number(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>
                      Team Capacity: {simForm.teamCapacityCount} Engineers
                    </label>
                    <input
                      type="range"
                      min="2"
                      max="15"
                      value={simForm.teamCapacityCount}
                      onChange={(e) => setSimForm({ ...simForm, teamCapacityCount: Number(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>

                <button
                  className="pulse-nav-pill-btn active"
                  style={{ width: '100%', padding: '10px', fontSize: 13 }}
                  disabled={simulating}
                  onClick={handleRunSimulation}
                >
                  {simulating ? 'Calculating deterministic delivery models...' : 'Run Simulation'}
                </button>

                {simResult && (
                  <div className="pulse-sim-result">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>Simulation Result:</span>
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: simResult.feasibilityPct >= 75 ? '#10b981' : '#f59e0b',
                        }}
                      >
                        {simResult.feasibilityPct}% Feasible
                      </span>
                    </div>

                    <div style={{ fontSize: 12, marginTop: 8 }}>
                      Projected Completion: <b>{simResult.projectedCompletionDate}</b> • Estimated Cost: $
                      <b>{simResult.projectedCost.toLocaleString()}</b>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <div className="pulse-sim-header">RECOMMENDED STRATEGIES:</div>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12 }}>
                        {simResult.recommendations.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 5: DECISION MEMORY (ADR)
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {modalMode === 'decisions' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={18} style={{ color: '#8b5cf6' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>Decision Memory (Architecture Decision Records)</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                <form
                  onSubmit={handleCreateDecision}
                  className="pulse-adr-form"
                >
                  <div className="pulse-modal-section-title">Record New Decision (ADR)</div>
                  <input
                    type="text"
                    placeholder="Decision Title (e.g. Adopt Vitest for Sub-second Backend Test Feedback)"
                    value={newDecision.title}
                    onChange={(e) => setNewDecision({ ...newDecision, title: e.target.value })}
                    className="pulse-modal-field"
                    required
                  />
                  <textarea
                    placeholder="Context & Rationale..."
                    value={newDecision.decision}
                    onChange={(e) => setNewDecision({ ...newDecision, decision: e.target.value })}
                    className="pulse-modal-field"
                    style={{ minHeight: 60 }}
                    required
                  />
                  <button
                    type="submit"
                    className="pulse-nav-pill-btn active"
                    style={{ padding: '6px 14px', fontSize: 12 }}
                  >
                    Save Decision
                  </button>
                </form>

                <div className="pulse-modal-section-title" style={{ marginTop: 16 }}>Approved Team Decisions:</div>
                {decisions.map((dec) => (
                  <div
                    key={dec.id}
                    className="pulse-adr-card"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{dec.title}</span>
                      <span className="pulse-confidence-pill high">
                        {dec.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="pulse-adr-meta">
                      Owner: {dec.ownerName} • {dec.date}
                    </div>
                    <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>{dec.decision}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL 6: DAILY STANDUP ASYNC BRIEFING
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {modalMode === 'standup' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar size={18} style={{ color: '#10b981' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>Async Daily Standup Briefing</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                <p className="pulse-modal-subtext">
                  Review, edit, or copy the automated daily briefing generated from recent commits, PR events, and blockers.
                </p>

                <textarea
                  value={standupContent}
                  onChange={(e) => setStandupContent(e.target.value)}
                  className="pulse-standup-textarea"
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                  <button
                    className="pulse-prompt-chip"
                    onClick={() => {
                      navigator.clipboard.writeText(standupContent);
                      setCopiedStandup(true);
                      setTimeout(() => setCopiedStandup(false), 2000);
                    }}
                  >
                    <Copy size={13} />
                    <span>{copiedStandup ? 'Copied!' : 'Copy to Clipboard'}</span>
                  </button>
                  <button
                    className="pulse-nav-pill-btn active"
                    style={{ padding: '6px 16px', fontSize: 12 }}
                    onClick={async () => {
                      await handleSaveStandup();
                      setModalMode('none');
                    }}
                  >
                    Save Standup
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* ── LUMI REAL AI CONFIGURATION MODAL ── */}
        {modalMode === 'deepseek_settings' && (
          <div className="pulse-modal-overlay" onClick={() => setModalMode('none')}>
            <motion.div
              className="pulse-modal-panel"
              style={{ maxWidth: 560 }}
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="pulse-modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Zap size={18} style={{ color: '#10b981' }} />
                  <span style={{ fontWeight: 800, fontSize: 16 }}>Lumi AI Configuration</span>
                </div>
                <button
                  onClick={() => setModalMode('none')}
                  className="pulse-modal-close-btn"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="pulse-modal-body">
                <p className="pulse-modal-subtext">
                  Lumi AI connects with neural models for code analysis, sprint forecasting, and architecture pair programming.
                </p>

                {/* Model Selection */}
                <div className="pulse-modal-section-title">
                  Select AI Model Mode
                </div>
                <div className="pulse-deepseek-model-grid">
                  <div
                    className={`pulse-deepseek-model-card ${deepseekModelSelected === 'deepseek-chat' ? 'selected' : ''}`}
                    onClick={() => {
                      setDeepseekModelSelected('deepseek-chat');
                      setDeepSeekModel('deepseek-chat');
                    }}
                  >
                    <div className="pulse-model-card-title">
                      <span>Standard Chat (Fast)</span>
                      <span className="pulse-model-badge-tag">Recommended</span>
                    </div>
                    <div className="pulse-model-card-desc">
                      Lightning-fast responses, optimal for everyday engineering questions, sprint telemetry, and code implementation.
                    </div>
                  </div>

                  <div
                    className={`pulse-deepseek-model-card ${deepseekModelSelected === 'deepseek-reasoner' ? 'selected' : ''}`}
                    onClick={() => {
                      setDeepseekModelSelected('deepseek-reasoner');
                      setDeepSeekModel('deepseek-reasoner');
                    }}
                  >
                    <div className="pulse-model-card-title">
                      <span>Deep Reasoning</span>
                      <span className="pulse-model-badge-tag" style={{ background: '#ede9fe', color: '#7c3aed' }}>
                        Reasoner
                      </span>
                    </div>
                    <div className="pulse-model-card-desc">
                      Advanced chain-of-thought reasoning, exceptional for complex algorithms, math, architecture, and debugging.
                    </div>
                  </div>
                </div>

                {/* API Key Status */}
                <div className="pulse-key-input-container">
                  <label className="pulse-modal-section-title">
                    Active Environment Key
                  </label>
                  <div className="pulse-key-input-wrapper">
                    <input
                      type={showKeySecret ? 'text' : 'password'}
                      placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      value={keyInputValue || getDeepSeekKey()}
                      onChange={(e) => setKeyInputValue(e.target.value)}
                    />
                    <button
                      type="button"
                      className="pulse-key-visibility-toggle"
                      onClick={() => setShowKeySecret((prev) => !prev)}
                      title={showKeySecret ? 'Hide Key' : 'Show Key'}
                    >
                      {showKeySecret ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Configured in environment (sk-••••c003)</span>
                    <span style={{ color: '#10b981', fontWeight: 600 }}>✓ Key Active in Environment</span>
                  </div>
                </div>

                {/* Test Connection Feedback */}
                {testFeedback && (
                  <div className={`pulse-test-banner ${testFeedback.success ? 'success' : 'error'}`}>
                    {testFeedback.success ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                    <span>{testFeedback.message}</span>
                  </div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
                  <button
                    type="button"
                    className="pulse-prompt-chip"
                    disabled={testingKey || !keyInputValue.trim()}
                    onClick={async () => {
                      setTestingKey(true);
                      setTestFeedback(null);
                      const res = await testDeepSeekConnection(keyInputValue);
                      setTestingKey(false);
                      setTestFeedback(res);
                    }}
                  >
                    <Zap size={13} />
                    <span>{testingKey ? 'Testing Connection...' : 'Test Connection'}</span>
                  </button>

                  <div style={{ display: 'flex', gap: 10 }}>
                    {deepseekKeyConfigured && (
                      <button
                        type="button"
                        style={{
                          padding: '8px 14px',
                          fontSize: 12,
                          borderRadius: 8,
                          border: '1px solid #ef4444',
                          background: 'transparent',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                        onClick={() => {
                          setDeepSeekKey('');
                          setKeyInputValue('');
                          setDeepseekKeyConfigured(false);
                          setTestFeedback({ success: false, message: 'DeepSeek key removed.' });
                          showToast?.('DeepSeek key removed.');
                        }}
                      >
                        Remove Key
                      </button>
                    )}

                    <button
                      type="button"
                      className="pulse-nav-pill-btn active"
                      style={{ padding: '8px 20px', fontSize: 12 }}
                      onClick={() => {
                        if (!keyInputValue.trim()) {
                          setTestFeedback({ success: false, message: 'Please enter a valid DeepSeek API key.' });
                          return;
                        }
                        setDeepSeekKey(keyInputValue);
                        setDeepSeekModel(deepseekModelSelected);
                        setDeepseekKeyConfigured(true);
                        showToast?.('DeepSeek AI activated successfully!');
                        setModalMode('none');
                      }}
                    >
                      Save & Activate
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
